import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";

import type { SupportedMedications } from "../../../catalogs/supported-medications";

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../components/ui-kit/base/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui-kit/base/popover";
import { ComboboxTrigger } from "../../../components/ui-kit/fields/base-combobox";
import { useFormFieldId } from "../../../components/ui-kit/form/form-field-id-context";
import { findMedicationMatches } from "./find-medication-match";

/**
 * Search-first medication picker across the whole catalog (no prior group
 * selection). The box shows nothing until the user types — the catalog is too
 * large to browse — then lists fuzzy-ranked matches grouped by medication group
 * so near-misses surface. Selecting a match fills group + name. A medication not
 * in the list can be entered as-is via the pinned footer, after which the caller
 * collects a group.
 */
export const MedicationCombobox = observer(function MedicationCombobox({
  medications,
  value,
  onSelect,
  onCustom,
}: {
  medications: SupportedMedications;
  value: string | null;
  onSelect: (group: string, name: string) => void;
  onCustom: (name: string) => void;
}) {
  const { t } = useLingui();
  const formFieldId = useFormFieldId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const trimmed = query.trim();
  // Searching builds a fuzzy index over the whole catalog, so keep it off the
  // render path: the row re-renders on every field change, but the results only
  // depend on the query.
  const { matchesByGroup, canUseAsTyped } = useMemo(() => {
    const matches = findMedicationMatches(trimmed, medications.items, {
      maxResults: 25,
      minLength: 1,
    });
    // Cluster the ranked matches under their medication group (best-matching
    // group first, ranking preserved within each) for grouped headings.
    const byGroup = new Map<string, string[]>();
    for (const m of matches) {
      const names = byGroup.get(m.group);
      if (names) names.push(m.name);
      else byGroup.set(m.group, [m.name]);
    }
    return {
      matchesByGroup: byGroup,
      canUseAsTyped:
        trimmed.length > 0 &&
        !medications.items.some(
          (m) => m.name.toLowerCase() === trimmed.toLowerCase(),
        ),
    };
  }, [trimmed, medications]);

  function close() {
    setOpen(false);
    setQuery("");
  }

  function select(group: string, name: string) {
    onSelect(group, name);
    close();
  }

  function chooseCustom() {
    onCustom(trimmed);
    close();
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <ComboboxTrigger
          id={formFieldId}
          label={value}
          placeholder={t`Search medication…`}
          open={open}
          className="min-w-[280px]"
        />
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t`Search medication…`}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList
            // The enclosing modal Dialog's scroll lock cancels wheel/touch
            // scroll outside itself via bubble-phase document listeners;
            // stopping propagation here lets the menu scroll natively.
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {trimmed === "" ? (
              <p className="text-muted-foreground py-6 text-center text-sm">
                <Trans>Type to search medications</Trans>
              </p>
            ) : matchesByGroup.size > 0 ? (
              [...matchesByGroup.entries()].map(([group, names]) => (
                <CommandGroup key={group} heading={group}>
                  {names.map((name) => (
                    <CommandItem
                      key={name}
                      value={`${group}/${name}`}
                      onSelect={() => select(group, name)}
                    >
                      {name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))
            ) : (
              <p className="text-muted-foreground py-6 text-center text-sm">
                <Trans>No matching medications</Trans>
              </p>
            )}
          </CommandList>
          {canUseAsTyped && (
            <div className="border-t p-1">
              <button
                type="button"
                onClick={chooseCustom}
                className="hover:bg-accent hover:text-accent-foreground flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm"
              >
                <Trans>Use “{trimmed}”</Trans>
                <span className="text-muted-foreground ml-2">
                  <Trans>— not in the list</Trans>
                </span>
              </button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
});
